import type { IEmailCampaignDraft } from "api/rpc/2024-04/facilityAdmin/marketing/emailBuilderTypes";

const EMAIL_BUILDER_HTTP_OK = 200;
const EMAIL_BUILDER_HTTP_CONFLICT = 409;

type TDraftGetResult = {
  status: number;
  data: IEmailCampaignDraft;
  message?: unknown;
};

type TDraftPostResult = {
  status: number;
  data: { data: IEmailCampaignDraft };
  message?: unknown;
};

function campaignDraftFromResponse(data: unknown): IEmailCampaignDraft | null {
  if (typeof data === "object" && data !== null && typeof (data as IEmailCampaignDraft).id === "number") {
    return data as IEmailCampaignDraft;
  }

  return null;
}

function apiErrorMessage(message: unknown, fallback: string): string {
  if (typeof message === "string" && message.length > 0) {
    return message;
  }

  return fallback;
}

export type TSelectEmailBuilderTemplateResult =
  | { ok: true; draft: IEmailCampaignDraft }
  | { ok: false; message: string };

export async function selectEmailBuilderTemplateForCampaign(options: {
  campaignId: number;
  templateId: number;
  createFromTemplate: (
    campaignId: number,
    params: { template_id: number },
    useGlobalLoader: boolean,
  ) => Promise<TDraftPostResult>;
  getDraft: (campaignId: number, useGlobalLoader: boolean) => Promise<TDraftGetResult>;
}): Promise<TSelectEmailBuilderTemplateResult> {
  const createRes = await options.createFromTemplate(
    options.campaignId,
    { template_id: options.templateId },
    false,
  );

  const createdDraft = campaignDraftFromResponse(createRes.data?.data);
  if (createRes.status === EMAIL_BUILDER_HTTP_OK && createdDraft != null) {
    return { ok: true, draft: createdDraft };
  }

  if (createRes.status === EMAIL_BUILDER_HTTP_CONFLICT) {
    const existingRes = await options.getDraft(options.campaignId, false);
    const existingDraft = campaignDraftFromResponse(existingRes.data);
    if (existingRes.status === EMAIL_BUILDER_HTTP_OK && existingDraft != null) {
      return { ok: true, draft: existingDraft };
    }

    return {
      ok: false,
      message: apiErrorMessage(existingRes.message, "Could not load the existing email campaign draft."),
    };
  }

  return {
    ok: false,
    message: apiErrorMessage(createRes.message, "Could not create email campaign draft from template."),
  };
}
